import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle, Clock } from 'lucide-react';
import { useAlert } from '../../hooks/useAlerts';
import { useResolve } from '../../hooks/useAcknowledge';
import AlertTimeline from '../../components/alerts/AlertTimeline';
import AcknowledgeModal from '../../components/alerts/AcknowledgeModal';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import clsx from 'clsx';

export default function AlertDetailPage() {
  const { alertId } = useParams<{ alertId: string }>();
  const { t } = useTranslation();
  const { data: alert, isLoading } = useAlert(alertId);
  const resolveMutation = useResolve();
  const [isAcknowledgeModalOpen, setIsAcknowledgeModalOpen] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('default', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const handleResolve = async () => {
    if (!alertId) return;
    try {
      await resolveMutation.mutateAsync(alertId);
    } catch (error) {
      console.error('Failed to resolve alert:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="space-y-6">
        <Link to="/alerts" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700">
          <ArrowLeft className="w-4 h-4" />
          {t('common.backToList')}
        </Link>
        <Card>
          <p className="text-gray-500 text-center py-8">{t('common.noData')}</p>
        </Card>
      </div>
    );
  }

  const getSeverityColor = (severity: string) => {
    return severity === 'critical' ? 'text-red-600' : 'text-yellow-600';
  };

  const getSeverityBgColor = (severity: string) => {
    return severity === 'critical' ? 'bg-red-50' : 'bg-yellow-50';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/alerts" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700">
          <ArrowLeft className="w-4 h-4" />
          {t('common.backToList')}
        </Link>

        <div className="flex items-center gap-3">
          {!alert.acknowledged_at && !alert.resolved_at && (
            <Button
              variant="secondary"
              onClick={() => setIsAcknowledgeModalOpen(true)}
              className="flex items-center gap-2"
            >
              <Clock className="w-4 h-4" />
              {t('alerts.acknowledgeModal.title')}
            </Button>
          )}

          {alert.acknowledged_at && !alert.resolved_at && (
            <Button
              variant="primary"
              onClick={handleResolve}
              disabled={resolveMutation.isPending}
              className="flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              {resolveMutation.isPending ? t('common.loading') : t('alerts.resolve.button')}
            </Button>
          )}
        </div>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t(`alerts.types.${alert.alert_type}`)}
        </h1>
        <div className="flex items-center gap-3">
          <span
            className={clsx(
              'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
              getSeverityColor(alert.severity),
              getSeverityBgColor(alert.severity)
            )}
          >
            {t(`alerts.filters.${alert.severity}`)}
          </span>
          {alert.resolved_at ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium text-green-600 bg-green-50">
              <CheckCircle className="w-4 h-4" />
              {t('alerts.resolved')}
            </span>
          ) : alert.acknowledged_at ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium text-blue-600 bg-blue-50">
              <Clock className="w-4 h-4" />
              {t('alerts.acknowledged')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium text-red-600 bg-red-50">
              {t('alerts.active')}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('alerts.details')}</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">{t('alerts.info.probe')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{alert.probe_name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t('alerts.info.logger')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{alert.logger_name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t('alerts.info.site')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{alert.site_name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t('alerts.info.type')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{t(`alerts.types.${alert.alert_type}`)}</dd>
            </div>
            {alert.threshold_value !== null && alert.threshold_value !== undefined && (
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('alerts.info.threshold')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{alert.threshold_value}</dd>
              </div>
            )}
            {alert.measured_value !== null && alert.measured_value !== undefined && (
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('alerts.info.measuredValue')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{alert.measured_value}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">{t('alerts.info.triggeredAt')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(alert.triggered_at)}</dd>
            </div>
            {alert.message && (
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('alerts.info.message')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{alert.message}</dd>
              </div>
            )}
          </dl>
        </Card>

        <Card>
          <AlertTimeline alert={alert} />
        </Card>
      </div>

      {alertId && (
        <AcknowledgeModal
          alertId={alertId}
          isOpen={isAcknowledgeModalOpen}
          onClose={() => setIsAcknowledgeModalOpen(false)}
        />
      )}
    </div>
  );
}
